import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";
import { m, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  CheckCheck,
  FileSpreadsheet,
  Paperclip,
  RotateCcw,
  ScanLine,
  Sparkles,
} from "lucide-react";
import { clsx } from "clsx";

/**
 * «Cómo funciona»: de la foto al dato, con la salida real.
 *
 * Reescrita el 16-9-2026. La versión anterior era una cronología vertical de 6
 * pasos en zigzag con una línea central que se rellenaba al hacer scroll —
 * EXACTAMENTE el mismo recurso que `WhatsAppAnimationSection`, que va justo
 * encima. Y no solo se parecían: los textos se repetían casi palabra por
 * palabra («Procesamiento con OCR e IA», «Extracción de campos», «Listo para
 * integrar»). La página contaba lo mismo dos veces seguidas.
 *
 * Esta sección tiene ahora otro trabajo: ENSEÑAR la salida. Tres paneles en
 * horizontal —documento, campos con su confianza, fila en su Excel— y una
 * animación de otro lenguaje visual: una línea de escaneo recorre la factura y,
 * al pasar por cada dato, lo resalta y lo rellena en el panel de campos.
 *
 * Los valores NO son inventados: son los que devuelve el extractor para
 * `public/samples/factura-ejemplo.pdf`, consultados en `scanner_results`. Quien
 * pulse «Ver un ejemplo con una factura de muestra» en el widget del hero
 * obtiene exactamente esto. Si cambia la factura de muestra, hay que
 * actualizarlos.
 *
 * Todo el texto está en el DOM desde el principio; la animación solo cambia
 * opacidad y resaltado. El prerender y los buscadores lo leen entero.
 */

// Salida real de `factura-ejemplo.pdf` (resultado bacefa29…, 16-9-2026).
const SAMPLE = [
  { name: "supplier_name", value: "Suministros Marbella S.L.", confidence: 0.99, kind: "text" },
  { name: "supplier_tax_id", value: "B-11223344", confidence: 0.97, kind: "text" },
  { name: "invoice_number", value: "F-2026-0412", confidence: 0.95, kind: "text" },
  { name: "issue_date", value: "2026-08-12", confidence: 0.98, kind: "date" },
  { name: "tax_amount", value: 145.28, confidence: 0.95, kind: "money" },
  { name: "total_amount", value: 837.08, confidence: 0.96, kind: "money" },
];
// Columnas visibles de la hoja. Solo cuatro: con las seis del extractor todo
// salía truncado («B-29…», «837,…») en el ancho de un tercio de pantalla. Son
// las que un gestor mira para cuadrar el trimestre; NIF y nº ya se ven en el
// panel de campos.
const SHEET_COLS = ["supplier_name", "issue_date", "tax_amount", "total_amount"];

// Facturas que «ya estaban» en la hoja antes de que llegue la de muestra.
// Ilustrativas; el IVA cuadra al 21 % para que un gestor no vea un error.
const PREVIOUS_ROWS = [
  { supplier_name: "Telefónica de España S.A.U.", issue_date: "2026-08-01", tax_amount: 9.45, total_amount: 54.45 },
  { supplier_name: "Papelería Costa S.L.", issue_date: "2026-08-03", tax_amount: 39.27, total_amount: 226.27 },
  { supplier_name: "Limpiezas Brillo S.L.", issue_date: "2026-08-05", tax_amount: 31.5, total_amount: 181.5 },
  { supplier_name: "Gasóleos del Sur S.A.", issue_date: "2026-08-07", tax_amount: 86.1, total_amount: 496.1 },
  { supplier_name: "Transportes Rápidos S.L.", issue_date: "2026-08-09", tax_amount: 67.2, total_amount: 387.2 },
];
const EMPTY_ROWS = 8; // filas vacías debajo, como en una hoja real
const BASE_AMOUNT = 691.8; // 837,08 − 145,28. Sale en la factura, no se extrae.

const LOCALE = { es: "es-ES", en: "en-GB", it: "it-IT", pt: "pt-PT", de: "de-DE", fr: "fr-FR" };

// Fases de la animación, en «pasos»:
//   0            reposo
//   1            llega el documento por WhatsApp
//   2 … 7        se extrae el campo i  (i = paso − 2)
//   8 … 11       se rellena la celda j de la fila nueva (j = paso − 8)
const FIELD_START = 2;
const ROW_START = FIELD_START + SAMPLE.length;
const LAST_STEP = ROW_START + SHEET_COLS.length;

const DELAY = { arrive: 350, field: 620, cell: 170 };

function useSequence(active, reduced) {
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    if (reduced) {
      setStep(LAST_STEP);
      return undefined;
    }
    setStep(0);
    const timers = [];
    let at = DELAY.arrive;
    timers.push(setTimeout(() => setStep(1), at));
    for (let s = FIELD_START; s <= LAST_STEP; s++) {
      at += s < ROW_START ? DELAY.field : DELAY.cell;
      timers.push(setTimeout(() => setStep(s), at));
    }
    return () => timers.forEach(clearTimeout);
  }, [active, reduced, run]);

  return { step, replay: () => setRun((n) => n + 1) };
}

export default function HowItWorksSection() {
  const { t, lang } = useTranslation();
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.3 });
  const reduced = useReducedMotion();
  const { step, replay } = useSequence(inView, reduced);

  const locale = LOCALE[lang] || LOCALE.en;
  const money = (n) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(n);
  const date = (iso) =>
    new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(
      new Date(`${iso}T12:00:00`)
    );
  const display = (f) => (f.kind === "money" ? money(f.value) : f.kind === "date" ? date(f.value) : f.value);
  const label = (name) => t(`free.fields.${name}`, { defaultValue: name });
  const byName = Object.fromEntries(SAMPLE.map((f) => [f.name, f]));
  const isMoney = (name) => byName[name].kind === "money";
  // En la hoja, fecha corta (12/08/26): con el año completo no cabía en su columna.
  const cell = (f) =>
    f.kind === "date"
      ? new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "2-digit" }).format(
          new Date(`${f.value}T12:00:00`)
        )
      : display(f);

  const arrived = step >= 1;
  const extracting = step >= FIELD_START && step < ROW_START;
  const activeField = extracting ? step - FIELD_START : -1;
  const fieldDone = (i) => step >= FIELD_START + i;
  const cellDone = (j) => step >= ROW_START + j;
  const finished = step >= LAST_STEP;

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="relative py-24 md:py-32 bg-white border-t border-gray-100 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Cabecera */}
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 border border-primary-100 text-sm font-medium text-primary-700 mb-5">
            <Sparkles className="w-4 h-4" />
            {t("how_it_works.badge")}
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-5">
            {t("how_it_works.title")}
          </h2>
          <p className="text-lg text-gray-600">{t("how_it_works.lead")}</p>
        </div>

        {/* Tubería: documento → campos → fila en su Excel */}
        <div className="grid gap-4 lg:gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch">
          {/* ---------- 1. Documento ---------- */}
          <Stage n={1} title={t("how_it_works.stages.1.title")} body={t("how_it_works.stages.1.body")} lit={arrived}>
            <InvoiceMock
              t={t}
              arrived={arrived}
              extracting={extracting}
              activeField={activeField}
              fieldDone={fieldDone}
              display={display}
              baseAmount={money(BASE_AMOUNT)}
            />
          </Stage>

          <Connector on={step >= FIELD_START} />

          {/* ---------- 2. Campos ---------- */}
          <Stage n={2} title={t("how_it_works.stages.2.title")} body={t("how_it_works.stages.2.body")} lit={step >= FIELD_START}>
            <div className="rounded-2xl border border-gray-200 bg-white p-4 h-full">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t("how_it_works.fields_label")}
                </span>
                <ScanLine className={clsx("w-4 h-4", extracting ? "text-primary-500 animate-pulse" : "text-gray-300")} />
              </div>
              <ul className="space-y-2.5">
                {SAMPLE.map((f, i) => (
                  <li
                    key={f.name}
                    className={clsx(
                      "rounded-xl px-3 py-2 transition-colors duration-300",
                      activeField === i ? "bg-primary-50 ring-1 ring-primary-200" : "bg-gray-50"
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs text-gray-500 shrink-0">{label(f.name)}</span>
                      <span
                        className={clsx(
                          "text-sm font-semibold text-gray-900 text-right truncate transition-opacity duration-300",
                          fieldDone(i) ? "opacity-100" : "opacity-0"
                        )}
                      >
                        {display(f)}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary-500 transition-[width] duration-500 ease-out"
                          style={{ width: fieldDone(i) ? `${f.confidence * 100}%` : "0%" }}
                        />
                      </div>
                      <span
                        className={clsx(
                          "text-[11px] tabular-nums text-gray-500 w-20 text-right transition-opacity duration-300",
                          fieldDone(i) ? "opacity-100" : "opacity-0"
                        )}
                      >
                        {f.confidence.toFixed(2)} {t("how_it_works.confidence")}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Stage>

          <Connector on={step >= ROW_START} />

          {/* ---------- 3. Salida: una fila más en su Excel ----------
              Antes era un JSON. Se cambió a petición del propietario, con razón:
              el cliente es una gestoría, no un desarrollador, y lo que quiere
              ver es que la factura acaba como una fila de su hoja de cálculo.
              La API sigue mencionada, pero como etiqueta secundaria. */}
          <Stage n={3} title={t("how_it_works.stages.3.title")} body={t("how_it_works.stages.3.body")} lit={step >= ROW_START}>
            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden h-full flex flex-col">
              {/* Barra de título estilo hoja de cálculo */}
              <div className="flex items-center gap-2 bg-[#107c41] px-3 py-2 text-white">
                <FileSpreadsheet className="w-4 h-4 shrink-0" />
                <span className="text-xs font-semibold truncate">{t("how_it_works.output_label")}</span>
              </div>

              <div className="flex-1 overflow-hidden">
                <table className="w-full table-fixed border-collapse text-[11px] leading-tight">
                  <colgroup>
                    <col className="w-7" />
                    <col className="w-[36%]" />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-100 text-gray-400">
                      <th className="border border-gray-200 font-normal py-0.5" />
                      {SHEET_COLS.map((name, c) => (
                        <th key={name} className="border border-gray-200 font-normal py-0.5">
                          {String.fromCharCode(65 + c)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Fila 1: cabeceras */}
                    <tr className="bg-gray-50 font-semibold text-gray-700">
                      <td className="border border-gray-200 bg-gray-100 text-center text-gray-400 font-normal">1</td>
                      {SHEET_COLS.map((name) => (
                        <td key={name} className={clsx("border border-gray-200 px-2 py-1.5 truncate", isMoney(name) && "text-right")}>
                          {t(`how_it_works.cols.${name}`)}
                        </td>
                      ))}
                    </tr>

                    {/* Facturas que ya estaban */}
                    {PREVIOUS_ROWS.map((row, r) => (
                      <tr key={r} className="text-gray-500">
                        <td className="border border-gray-200 bg-gray-100 text-center text-gray-400">{r + 2}</td>
                        {SHEET_COLS.map((name) => (
                          <td key={name} className={clsx("border border-gray-200 px-2 py-1.5 truncate", isMoney(name) && "text-right tabular-nums")}>
                            {cell({ ...byName[name], value: row[name] })}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {/* La factura que acaba de llegar, celda a celda */}
                    <tr
                      className={clsx(
                        "transition-colors duration-500",
                        step >= ROW_START && !finished ? "bg-primary-50" : finished ? "bg-primary-50/50" : "bg-white"
                      )}
                    >
                      <td className="border border-gray-200 bg-gray-100 text-center text-gray-400">{PREVIOUS_ROWS.length + 2}</td>
                      {SHEET_COLS.map((name, j) => (
                        <td
                          key={name}
                          title={String(cell(byName[name]))}
                          className={clsx(
                            "border px-2 py-1.5 truncate font-semibold text-gray-900",
                            isMoney(name) && "text-right tabular-nums",
                            step === ROW_START + j ? "border-[#107c41] ring-1 ring-inset ring-[#107c41]" : "border-gray-200"
                          )}
                        >
                          <span className={clsx("transition-opacity duration-200", cellDone(j) ? "opacity-100" : "opacity-0")}>
                            {cell(byName[name])}
                          </span>
                        </td>
                      ))}
                    </tr>

                    {/* Filas vacías */}
                    {Array.from({ length: EMPTY_ROWS }, (_, e) => (
                      <tr key={`e${e}`}>
                        <td className="border border-gray-200 bg-gray-100 text-center text-gray-400">
                          {PREVIOUS_ROWS.length + 3 + e}
                        </td>
                        {SHEET_COLS.map((name) => (
                          <td key={name} className="border border-gray-200 py-1.5">&nbsp;</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-3 py-2.5 border-t border-gray-100 flex flex-wrap items-center gap-1.5">
                {["Excel", "CSV", "API"].map((fmt, k) => (
                  <span
                    key={fmt}
                    className={clsx(
                      "px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors duration-300",
                      finished && k === 0
                        ? "bg-[#107c41] text-white"
                        : finished
                        ? "bg-primary-50 text-primary-700"
                        : "bg-gray-100 text-gray-400"
                    )}
                  >
                    {fmt}
                  </span>
                ))}
                {finished && !reduced && (
                  <button
                    type="button"
                    onClick={replay}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    {t("how_it_works.replay")}
                  </button>
                )}
              </div>
            </div>
          </Stage>
        </div>
      </div>
    </section>
  );
}

/** Columna de la tubería: cabecera numerada + contenido. */
function Stage({ n, title, body, lit, children }) {
  return (
    <div className="flex flex-col min-w-0">
      <div className="flex items-start gap-3 mb-4 min-h-[4.5rem]">
        <span
          className={clsx(
            "shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-300",
            lit ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-400"
          )}
        >
          {n}
        </span>
        <div>
          <h3 className="font-bold text-gray-900 leading-snug">{title}</h3>
          <p className="text-sm text-gray-500 leading-snug mt-0.5">{body}</p>
        </div>
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

/** Flecha entre paneles: horizontal en escritorio, vertical en móvil. */
function Connector({ on }) {
  return (
    <div className="flex items-center justify-center lg:pt-[4.5rem]" aria-hidden="true">
      <div
        className={clsx(
          "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300",
          on ? "bg-primary-600 text-white scale-100" : "bg-gray-100 text-gray-300 scale-90"
        )}
      >
        <ArrowDown className="w-4 h-4 lg:hidden" />
        <ArrowRight className="w-4 h-4 hidden lg:block" />
      </div>
    </div>
  );
}

/**
 * Factura dibujada en HTML, no una imagen: nítida a cualquier tamaño, sin
 * petición extra y con texto real. Cada dato extraíble lleva un ref para que la
 * línea de escaneo se pose exactamente sobre él.
 */
function InvoiceMock({ t, arrived, extracting, activeField, fieldDone, display, baseAmount }) {
  const paperRef = useRef(null);
  const valueRefs = useRef([]);
  const [scanTop, setScanTop] = useState(0);

  useLayoutEffect(() => {
    if (activeField < 0) return;
    const el = valueRefs.current[activeField];
    const paper = paperRef.current;
    if (!el || !paper) return;
    const e = el.getBoundingClientRect();
    const p = paper.getBoundingClientRect();
    setScanTop(e.top - p.top + e.height / 2);
  }, [activeField]);

  // Resalta el dato mientras se lee y lo deja marcado en verde suave después.
  const mark = (i) =>
    clsx(
      "rounded px-1 -mx-1 transition-colors duration-300",
      activeField === i ? "bg-primary-200/80" : fieldDone(i) ? "bg-primary-50" : "bg-transparent"
    );
  const ref = (i) => (el) => {
    valueRefs.current[i] = el;
  };

  return (
    <div className="relative h-full rounded-2xl bg-gradient-to-br from-[#efeae2] to-[#e6ded3] p-4">
      {/* Burbuja de WhatsApp con el adjunto */}
      <m.div
        initial={false}
        animate={arrived ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 8, scale: 0.96 }}
        transition={{ type: "spring", stiffness: 380, damping: 26 }}
        className="ml-auto mb-3 w-fit max-w-full flex items-center gap-2 rounded-lg rounded-tr-none bg-[#d9fdd3] px-3 py-1.5 shadow-sm"
      >
        <Paperclip className="w-3.5 h-3.5 text-gray-500 shrink-0" />
        <span className="text-xs text-gray-700 truncate">IMG_2041.jpg</span>
        <CheckCheck className="w-3.5 h-3.5 text-sky-500 shrink-0" />
      </m.div>

      {/* Papel, ligeramente girado como una foto real */}
      <div
        ref={paperRef}
        className={clsx(
          "relative overflow-hidden rounded-md bg-white p-4 shadow-md text-[11px] leading-snug text-gray-700 transition-transform duration-500",
          arrived ? "-rotate-1" : "rotate-0"
        )}
      >
        {/* Línea de escaneo */}
        <div
          aria-hidden="true"
          className={clsx(
            "pointer-events-none absolute left-0 right-0 h-8 -translate-y-1/2 transition-[top,opacity] duration-500 ease-out",
            extracting ? "opacity-100" : "opacity-0"
          )}
          style={{ top: scanTop }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-400/15 to-transparent" />
          <div className="absolute left-0 right-0 top-1/2 h-px bg-primary-500 shadow-[0_0_8px_2px_rgba(26,160,13,0.45)]" />
        </div>

        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="font-bold text-gray-900 text-[12px]">
              <span ref={ref(0)} className={mark(0)}>{display({ kind: "text", value: "Suministros Marbella S.L." })}</span>
            </div>
            <div className="text-gray-500 mt-0.5">
              NIF <span ref={ref(1)} className={mark(1)}>B-11223344</span>
            </div>
          </div>
          <span className="font-black tracking-widest text-gray-300 text-[10px] shrink-0">
            {t("how_it_works.invoice_word")}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-1 mb-3">
          <div>
            Nº <span ref={ref(2)} className={clsx("font-semibold", mark(2))}>F-2026-0412</span>
          </div>
          <div className="text-right">
            <span ref={ref(3)} className={mark(3)}>{display({ kind: "date", value: "2026-08-12" })}</span>
          </div>
        </div>

        {/* Líneas de detalle: no se extraen, dan realismo */}
        <div className="space-y-1.5 mb-3 border-y border-dashed border-gray-200 py-2.5">
          {[78, 64, 71].map((w, k) => (
            <div key={k} className="flex items-center gap-2">
              <div className="h-1.5 rounded-full bg-gray-200" style={{ width: `${w}%` }} />
              <div className="ml-auto h-1.5 w-8 rounded-full bg-gray-200" />
            </div>
          ))}
        </div>

        <div className="space-y-0.5 text-right">
          <div className="text-gray-500">
            {t("how_it_works.base")} {baseAmount}
          </div>
          <div className="text-gray-500">
            IVA 21% <span ref={ref(4)} className={mark(4)}>{display({ kind: "money", value: 145.28 })}</span>
          </div>
          <div className="font-bold text-gray-900 text-[13px] pt-0.5">
            TOTAL <span ref={ref(5)} className={mark(5)}>{display({ kind: "money", value: 837.08 })}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
